import AccountDetails from './components/account/Account';
import FooterPrivacy from 'src/views/auth/components/baseTemplate/FooterPrivacy';
import { formatMoney } from 'src/utils/formatMoney';
import { IUser } from 'src/utils/interfaces/user';
import Paymentdetails from './components/payment/index';
import PayoffQuoteDetailsUS from './components/payoffQuoteDetailsUS';
import { Permissions } from 'src/utils/enums/permissions.enum';
import Print from './components/print/Print';
import QuoteDetails from './components/details/Details';
import QuoteInformation from './components/info/Information';
import { useLocalization } from 'src/utils/useLocalization';
import { useReactToPrint } from 'react-to-print';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ViewInformation from '../../payments/information/components/View';
import { getPermissionsMap, isAllow, isSiteCanadian } from 'src/utils/helpers';
import Notification, {
    NotificationType,
} from 'src/components/ui/notification/Notification';
import {
    PAYOFF_QUOTE_DETAILS_NOTE_CA,
    PAYOFF_QUOTE_DETAILS_NOTE_US,
} from '../../../../localization/resouceKeys';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './styles.scss';

const parseDecodedData = (encodedData: string | null) => {
    if (!encodedData) {
        return null;
    }

    try {
        return JSON.parse(atob(encodedData));
    } catch {
        return null;
    }
};

interface IPayoffQuoteDetailCAProps {
    resolvedData: any;
    accountDetail: any;
    quoteDetails: any;
    privacyFlag: boolean;
    accountType: string;
    brand: string;
    country: string;
    returnToSearch: () => void;
    goToMakePayment: () => void;
    isPaymentAllow: boolean;
}

const PayoffQuoteDetailCA = ({
    resolvedData,
    accountDetail,
    quoteDetails,
    privacyFlag,
    accountType,
    brand,
    country,
    returnToSearch,
    goToMakePayment,
    isPaymentAllow,
}: IPayoffQuoteDetailCAProps) => {
    const { payoffQuotesDetail, payoffQuotesNote, labelText } =
        useLocalization();
    const { t } = useTranslation();
    const componentRef = useRef<HTMLDivElement>(null);
    const [visibility, setVisibility] = useState<boolean>(true);

    const handlePrint = useReactToPrint({
        content: () => componentRef.current,
    });

    // Sync amountBeforeTax from quoteDetails into accountDetail for CA
    accountDetail.amountBeforeTax = quoteDetails.amountBeforeTax;

    return (
        <>
            <div style={{ display: 'none' }}>
                <Print
                    country={country}
                    brand={brand}
                    accountType={accountType}
                    account={accountDetail}
                    quote={quoteDetails}
                    ref={componentRef}
                />
            </div>
            {visibility && (
                <div className='u-mb-large warning'>
                    <Notification
                        content=''
                        contentHtml={payoffQuotesDetail.WARNING_MESSAGE}
                        type={NotificationType.WARNING}
                        visibility={visibility}
                        setVisibility={setVisibility}
                    />
                </div>
            )}
            <div className='title-wrapper'>
                <h5>
                    <span className='c-section-heading__title-text'>
                        {payoffQuotesDetail.TITLE}
                    </span>
                </h5>
                <div className='action-button'>
                    <button
                        className='c-btn c-btn--link c-btn--small c-btn--simple'
                        onClick={handlePrint}
                    >
                        <i
                            className='c-btn__icon c-icon c-icon--[semantic-print]'
                            aria-hidden='true'
                            role='img'
                        ></i>
                        <span className='c-btn__text'>
                            {labelText.LABEL_TEXT_PRINT}
                        </span>
                    </button>
                    {isPaymentAllow && (
                        <button
                            className='c-btn--secondary c-btn c-btn--small'
                            type='button'
                            onClick={goToMakePayment}
                        >
                            <span className='c-btn__text'>
                                {labelText.LABEL_TEXT_MENU_PAYMENTS_MAKE}
                            </span>
                        </button>
                    )}
                </div>
            </div>
            <div className='return-button u-mb-xsmall'>
                <button
                    className='c-btn c-btn--link c-btn--small'
                    type='button'
                    onClick={returnToSearch}
                >
                    <i className='c-btn__icon c-icon c-icon--[semantic-back]' />
                    <span className='c-btn__text'>
                        {payoffQuotesDetail.BUTTON}
                    </span>
                </button>
            </div>
            <QuoteInformation
                brand={brand}
                country={country}
                accountType={accountType}
                quote={quoteDetails}
            />
            <QuoteDetails
                accountType={accountType}
                privateFlag={privacyFlag}
                quote={quoteDetails}
            />
            {accountType === 'OperatingLease' && (
                <section className='c-form-section'>
                    <div className='c-form-section__content'>
                        <div className='title'>
                            <h6>
                                <span className='c-section-heading__title-text section-title'>
                                    {payoffQuotesNote.TITLE}
                                </span>
                            </h6>
                            <div className='quote-note'>
                                {country === 'Canada'
                                    ? t(PAYOFF_QUOTE_DETAILS_NOTE_CA, {
                                          purchaseOptionFee: formatMoney(
                                              quoteDetails.purchaseOptionFee,
                                          ),
                                      })
                                    : t(PAYOFF_QUOTE_DETAILS_NOTE_US, {
                                          taxableAmount: formatMoney(
                                              quoteDetails.taxableAmount,
                                          ),
                                          purchaseOptionFee: formatMoney(
                                              quoteDetails.purchaseOptionFee,
                                          ),
                                          total: formatMoney(
                                              quoteDetails.taxableAmount +
                                                  quoteDetails.purchaseOptionFee,
                                          ),
                                      })}
                            </div>
                        </div>
                    </div>
                </section>
            )}
            <ViewInformation data={resolvedData} isQuoteDetail={true} />
            <AccountDetails
                privateFlag={privacyFlag}
                account={accountDetail}
                quoteDetails={quoteDetails}
            />
            <Paymentdetails accountType={accountType} />
        </>
    );
};

// Routing component: decides whether to show CA or US payoff quote detail
const PayoffQuoteDetailComponent = () => {
    const [searchParams] = useSearchParams();
    const encodedData = searchParams.get('data');
    const isCanadianSite = isSiteCanadian();
    const resolvedData = parseDecodedData(encodedData);
    const navigate = useNavigate();
    const dealerNumber = useSelector((state: IUser) => {
        return state.dealerDataInfo?.dealer.dealerNumber;
    });

    useEffect(() => {
        if (!resolvedData && isCanadianSite) {
            navigate('/dashboard/payoff-quotes', { replace: true });
        }
    }, [navigate, resolvedData, isCanadianSite]);

    if (!resolvedData) {
        return null;
    }

    const { accountDetail, quoteDetails, privacyFlag } = resolvedData;
    const accountType = accountDetail.accountType;
    const brand = accountDetail.brand;
    const country = accountDetail.country;

    const returnToSearch = () => navigate('/dashboard/payoff-quotes');
    const goToMakePayment = () =>
        navigate('/dashboard/payments/make', {
            state: {
                from: 'payoff-quote/detail',
                accountNumber: accountDetail.accountNumber,
            },
        });

    const permissionsMap = getPermissionsMap();
    const isPaymentAllow = isAllow(
        [
            Permissions.DEALER_ELECTRONIC_PAYMENT_SUBMIT,
            Permissions.DEALER_ELECTRONIC_PAYMENT_SUBMIT_AND_APPROVE,
        ],
        permissionsMap,
        dealerNumber,
    );

    return (
        <div className='dashboard-content-wrapper'>
            <div className='dashboard-content-container'>
                <div className='payments-details-wrapper'>
                    <section className='o-content-section'>
                        {isCanadianSite ? (
                            <PayoffQuoteDetailCA
                                resolvedData={resolvedData}
                                accountDetail={accountDetail}
                                quoteDetails={quoteDetails}
                                privacyFlag={privacyFlag}
                                accountType={accountType}
                                brand={brand}
                                country={country}
                                returnToSearch={returnToSearch}
                                goToMakePayment={goToMakePayment}
                                isPaymentAllow={isPaymentAllow}
                            />
                        ) : (
                            <PayoffQuoteDetailsUS
                                accountType={accountType}
                                account={accountDetail}
                                onCancel={returnToSearch}
                                onGetPayoffQuote={returnToSearch}
                            />
                        )}
                    </section>
                    <section className='privacy-footer-container'>
                        <FooterPrivacy />
                    </section>
                </div>
            </div>
        </div>
    );
};

export default PayoffQuoteDetailComponent;
